"use client";

import { c as _c } from "react/compiler-runtime";
import { getUserPageTabByRoute } from "@/components/user/layout/userTabs.config";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import useCapacitor from "@/hooks/useCapacitor";
import { commonApiPost } from "@/services/api/common-api";
import type { DeviceInfo } from "@capacitor/device";
import { Device } from "@capacitor/device";
import type { PushNotificationSchema } from "@capacitor/push-notifications";
import { PushNotifications } from "@capacitor/push-notifications";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/navigation";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../auth/Auth";
import { getStableDeviceId } from "./stable-device-id";
const MAX_REGISTRATION_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 5000;
const IOS_INITIALIZATION_DELAY_MS = 500;
const DELEGATE_ERROR_PATTERNS = ["capacitorDidRegisterForRemoteNotifications", "didRegisterForRemoteNotifications"];
const isDelegateError = (error: unknown): boolean => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return DELEGATE_ERROR_PATTERNS.some(pattern => errorMessage.includes(pattern));
};
const registerWithRetry = async (maxRetries = MAX_REGISTRATION_RETRIES): Promise<void> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      await PushNotifications.register();
      return;
    } catch (registerError: unknown) {
      const isDelegate = isDelegateError(registerError);
      const hasRetriesLeft = attempt < maxRetries - 1;
      if (isDelegate && hasRetriesLeft) {
        const delay = Math.min(INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt), MAX_RETRY_DELAY_MS);
        console.warn(`iOS push notification registration attempt ${attempt + 1} failed. Retrying in ${delay}ms...`, registerError);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw registerError;
    }
  }
};
type NotificationsContextType = {
  removeWaveDeliveredNotifications: (waveId: string) => Promise<void>;
  removeAllDeliveredNotifications: () => Promise<void>;
};
const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);
const redirectConfig = {
  path: ({
    path
  }: {
    path: string;
  }) => `/${path}`,
  profile: ({
    handle,
    subroute
  }: {
    handle: string;
    subroute?: string;
  }) => {
    if (!subroute) return `/${handle}`;
    const validTab = getUserPageTabByRoute(subroute);
    if (!validTab) return `/${handle}`;
    return `/${handle}/${validTab.route}`;
  },
  "the-memes": ({
    id
  }: {
    id: string;
  }) => `/the-memes/${id}`,
  "6529-gradient": ({
    id
  }: {
    id: string;
  }) => `/6529-gradient/${id}`,
  "meme-lab": ({
    id
  }: {
    id: string;
  }) => `/meme-lab/${id}`,
  waves: ({
    wave_id,
    drop_id
  }: {
    wave_id: string;
    drop_id: string;
  }) => {
    const base = `/waves?wave=${wave_id}`;
    return drop_id ? `${base}&serialNo=${drop_id}` : base;
  }
};
interface NotificationData {
  redirect?: keyof typeof redirectConfig | undefined;
  profile_id?: string | undefined;
  path?: string | undefined;
  handle?: string | undefined;
  subroute?: string | undefined;
  id?: string | undefined;
  wave_id?: string | undefined;
  drop_id?: string | undefined;
  [key: string]: unknown;
}
export const NotificationsProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(28);
  const {
    children
  } = t0;
  const {
    isCapacitor,
    isIos,
    isActive
  } = useCapacitor();
  const {
    connectedProfile
  } = useAuth();
  const router = useRouter();
  const initializationRef = useRef(null);
  const isRegisteredRef = useRef(false);
  let t1;
  if ($[0] !== isIos) {
    t1 = async notifications => {
      if (isIos && isRegisteredRef.current) {
        ;
        try {
          await PushNotifications.removeDeliveredNotifications({
            notifications
          });
        } catch (t2) {
          const error = t2;
          console.error("Error removing delivered notifications", error);
        }
      }
    };
    $[0] = isIos;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const removeDeliveredNotifications = t1;
  let t2;
  if ($[2] !== removeDeliveredNotifications) {
    t2 = async (routerInstance, notification, profileInstance) => {
      const notificationData = notification.data ?? {};
      const notificationProfileId = notificationData.profile_id;
      if (profileInstance && notificationProfileId && notificationProfileId !== profileInstance.id) {
        console.warn("Notification profile id does not match connected profile");
        return;
      }
      await removeDeliveredNotifications([notification]);
      const redirectUrl = resolveRedirectUrl(notificationData);
      if (redirectUrl) {
        routerInstance.push(redirectUrl);
      } else {
        console.warn("No redirect url found in notification data", notificationData);
      }
    };
    $[2] = removeDeliveredNotifications;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handlePushNotificationAction = t2;
  let t3;
  if ($[4] !== handlePushNotificationAction || $[5] !== isIos || $[6] !== router) {
    t3 = async profile => {
      ;
      try {
        isRegisteredRef.current = false;
        await PushNotifications.removeAllListeners();
        const stableDeviceId = await getStableDeviceId();
        const deviceInfo = await Device.getInfo();
        await PushNotifications.addListener("registration", async token => {
          isRegisteredRef.current = true;
          await registerPushNotification(stableDeviceId, deviceInfo, token.value, profile);
        });
        await PushNotifications.addListener("registrationError", error_1 => {
          isRegisteredRef.current = false;
          console.error("Push registration error: ", error_1);
          Sentry.captureException(error_1, {
            tags: {
              component: "NotificationsProvider",
              operation: "pushRegistrationError"
            }
          });
        });
        await PushNotifications.addListener("pushNotificationReceived", _temp);
        await PushNotifications.addListener("pushNotificationActionPerformed", async action => {
          await handlePushNotificationAction(router, action.notification, profile);
        });
        const permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive === "granted") {
          if (isIos) {
            await new Promise(_temp2);
          }
          await registerWithRetry();
        } else {
          console.warn("Push notifications permission not granted");
        }
      } catch (t4) {
        const error_0 = t4;
        console.error("Error in initializePushNotifications", error_0);
        throw error_0;
      }
    };
    $[4] = handlePushNotificationAction;
    $[5] = isIos;
    $[6] = router;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const initializePushNotifications = t3;
  let t4;
  if ($[8] !== initializePushNotifications || $[9] !== isCapacitor) {
    t4 = async profile_0 => {
      if (isCapacitor) {
        await initializePushNotifications(profile_0);
      }
    };
    $[8] = initializePushNotifications;
    $[9] = isCapacitor;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const initializeNotifications = t4;
  let t5;
  let t6;
  if ($[11] !== connectedProfile || $[12] !== initializeNotifications || $[13] !== isActive || $[14] !== isCapacitor) {
    t5 = () => {
      const profileId = connectedProfile?.id ?? null;
      if (isCapacitor && isActive && initializationRef.current !== profileId) {
        initializationRef.current = profileId;
        initializeNotifications(connectedProfile ?? undefined).catch(error_2 => {
          console.error("Failed to initialize push notifications", error_2);
          Sentry.captureException(error_2, {
            tags: {
              component: "NotificationsProvider",
              operation: "initializeNotifications"
            }
          });
          initializationRef.current = null;
        });
      }
    };
    t6 = [connectedProfile, isCapacitor, isActive, initializeNotifications];
    $[11] = connectedProfile;
    $[12] = initializeNotifications;
    $[13] = isActive;
    $[14] = isCapacitor;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  if ($[17] !== isIos || $[18] !== removeDeliveredNotifications) {
    t7 = async waveId => {
      if (isIos && isRegisteredRef.current) {
        ;
        try {
          const deliveredNotifications = await PushNotifications.getDeliveredNotifications();
          const waveNotifications = deliveredNotifications.notifications.filter(notification_1 => notification_1.data?.wave_id === waveId);
          await removeDeliveredNotifications(waveNotifications);
        } catch (t8) {
          const error_3 = t8;
          console.error("Error removing wave delivered notifications", error_3);
          Sentry.captureException(error_3, {
            tags: {
              component: "NotificationsProvider",
              operation: "removeWaveDeliveredNotifications"
            }
          });
        }
      }
    };
    $[17] = isIos;
    $[18] = removeDeliveredNotifications;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const removeWaveDeliveredNotifications = t7;
  let t8;
  if ($[20] !== isIos) {
    t8 = async () => {
      if (isIos && isRegisteredRef.current) {
        ;
        try {
          await PushNotifications.removeAllDeliveredNotifications();
        } catch (t9) {
          const error_4 = t9;
          console.error("Error removing all delivered notifications", error_4);
          Sentry.captureException(error_4, {
            tags: {
              component: "NotificationsProvider",
              operation: "removeAllDeliveredNotifications"
            }
          });
        }
      }
    };
    $[20] = isIos;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const removeAllDeliveredNotifications = t8;
  let t9;
  if ($[22] !== removeAllDeliveredNotifications || $[23] !== removeWaveDeliveredNotifications) {
    t9 = {
      removeWaveDeliveredNotifications,
      removeAllDeliveredNotifications
    };
    $[22] = removeAllDeliveredNotifications;
    $[23] = removeWaveDeliveredNotifications;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const value = t9;
  let t10;
  if ($[25] !== children || $[26] !== value) {
    t10 = <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
    $[25] = children;
    $[26] = value;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
};
const registerPushNotification = async (deviceId: string, deviceInfo: DeviceInfo, token: string, profile?: ApiIdentity) => {
  try {
    await commonApiPost({
      endpoint: `push-notifications/register`,
      body: {
        device_id: deviceId,
        token,
        platform: deviceInfo.platform,
        profile_id: profile?.id
      }
    });
  } catch (error) {
    console.error("Push registration error", error);
    Sentry.captureException(error, {
      tags: {
        component: "NotificationsProvider",
        operation: "registerPushNotification"
      }
    });
  }
};
const resolveRedirectUrl = (notificationData: NotificationData) => {
  const {
    redirect,
    ...params
  } = notificationData;
  if (!redirect) {
    console.warn("No redirect type found in notification data", notificationData);
    return null;
  }
  const resolveFn = redirectConfig[redirect];
  if (!resolveFn) {
    console.error("Unknown redirect type", redirect);
    return null;
  }
  try {
    return (resolveFn as (params: Record<string, unknown>) => string)(params);
  } catch (error) {
    console.error("Error resolving redirect URL", error);
    return null;
  }
};
export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotificationsContext must be used within a NotificationsProvider");
  }
  return context;
};
function _temp(notification_0) {
  console.warn("Push notification received: ", notification_0);
}
function _temp2(resolve) {
  return setTimeout(resolve, IOS_INITIALIZATION_DELAY_MS);
}